import { Drawer, ErrorPanel, LinearProgress } from "@octopusdeploy/design-system-components";
import { useInlineStatusMutation, useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { GitCredentialResource } from "@octopusdeploy/octopus-server-client";
import { cloneDeep } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AllowedRepositoriesFormSection } from "~/areas/library/components/GitCredentials/Components/AllowedRepositoriesFormSection";
import { createErrorInfoFromError } from "~/components/PageContent/createErrorInfo";
import { ExpandableFormSection, ExpansionButtons, Summary } from "~/components/form/index";
import type { DropdownMenuOption } from "~/primitiveComponents/form/Select/DropDownMenu";
import Select from "~/primitiveComponents/form/Select/Select";
type CommonProps = {
    additionalAllowedRepositoryUrls: string[] | undefined;
};
type ConnectExistingGitCredentialDrawerInnerProps = CommonProps & {
    selectedGitCredential: GitCredentialResource | undefined;
    setSelectedGitCredential: (value: React.SetStateAction<GitCredentialResource | undefined>) => void;
    onError: (error: Error) => void;
    isSaving: boolean;
};
const ConnectExistingGitCredentialDrawerInner = (props: ConnectExistingGitCredentialDrawerInnerProps) => {
    const { selectedGitCredential, setSelectedGitCredential, additionalAllowedRepositoryUrls, onError, isSaving } = props;
    const { result, isLoading, error } = useInlineStatusQuery(async (repo) => await repo.GitCredentials.list(), [], "List Git Credentials");
    const selectCloneAndUpdateFormModel = useCallback((id: string | undefined) => {
        let credential = result?.Items.find((gc) => gc.Id === id);
        if (credential) {
            //make sure we are working with a copy of this credential
            credential = cloneDeep(credential);
            credential.RepositoryRestrictions.Enabled = true;
            //produce a set that is the different between the existing repo's and the new set
            const newRepoUrls = new Set(additionalAllowedRepositoryUrls).difference(new Set(credential.RepositoryRestrictions.AllowedRepositories));
            if (newRepoUrls.size > 0) {
                credential.RepositoryRestrictions.AllowedRepositories = [...credential.RepositoryRestrictions.AllowedRepositories, ...newRepoUrls];
            }
        }
        setSelectedGitCredential(credential);
    }, [result?.Items, setSelectedGitCredential, additionalAllowedRepositoryUrls]);
    //when the results change, preload the first item
    useEffect(() => {
        if (result && result.Items.length > 0 && !selectedGitCredential) {
            selectCloneAndUpdateFormModel(result.Items[0].Id);
        }
    }, [result, selectCloneAndUpdateFormModel, selectedGitCredential]);
    useEffect(() => {
        if (error) {
            onError(error);
        }
    }, [error, onError]);
    const selectItems: DropdownMenuOption[] = useMemo(() => {
        if (!result) {
            return [];
        }
        return result.Items.map((cred) => ({
            value: cred.Id,
            text: cred.Name,
        }));
    }, [result]);
    const gitCredentialSummary = useMemo(() => Summary.summary(selectedGitCredential ? selectedGitCredential.Name : "Select a git credential"), [selectedGitCredential]);
    return (<>
            <LinearProgress show={isLoading || isSaving} variant={"indeterminate"}/>
            {!error && !isLoading && (<>
                    <ExpansionButtons expandAllOnMount={true} containerKey={"ConnectExistingGitCredentialDrawer"}/>
                    <ExpandableFormSection title={"Select Credential"} key={"Select Credential"} errorKey={"SelectCredential"} summary={gitCredentialSummary} isExpandedByDefault={true} help="Select the Git credential you want to connect." containerKey={"ConnectExistingGitCredentialDrawer"}>
                        <Select label={"Git credential"} items={selectItems} value={selectedGitCredential?.Id} onChange={selectCloneAndUpdateFormModel} allowFilter={true} allowClear={false}/>
                    </ExpandableFormSection>
                    {selectedGitCredential && (<AllowedRepositoriesFormSection repositoryRestrictions={selectedGitCredential.RepositoryRestrictions} onRepositoryRestrictionsUpdated={(updatedRestrictions) => setSelectedGitCredential({
                    ...selectedGitCredential,
                    RepositoryRestrictions: updatedRestrictions,
                })} containerKey={"ConnectExistingGitCredentialDrawer"}/>)}
                </>)}
        </>);
};
type ConnectExistingGitCredentialDrawerProps = CommonProps & {
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
};
export const ConnectExistingGitCredentialDrawer = (props: ConnectExistingGitCredentialDrawerProps) => {
    const { isOpen, onClose, onSave, ...rest } = props;
    const [selectedGitCredential, setSelectedGitCredential] = useState<GitCredentialResource | undefined>();
    const [error, setError] = useState<Error | undefined>();
    const { execute: save, isExecuting: isSaving } = useInlineStatusMutation<GitCredentialResource[], GitCredentialResource>({
        action: (repo, model) => repo.GitCredentials.save(model),
        name: "Connect Git Credential",
        onError: (error) => {
            setError(error);
        },
        afterComplete: () => {
            onSave();
            return Promise.resolve();
        },
    });
    const errorInfo = useMemo(() => error && createErrorInfoFromError(error), [error]);
    return (<Drawer variant={"default"} isOpen={isOpen} onClose={() => {
            //clear any previous errors
            setSelectedGitCredential(undefined);
            setError(undefined);
            onClose();
        }} title={"Connect an existing Git credential"} layout={"none"} primaryButtonLabel={isSaving ? "Saving" : "Save"} onPrimaryButtonClick={async () => {
            if (selectedGitCredential) {
                await save(selectedGitCredential);
            }
        }} isPrimaryButtonDisabled={!selectedGitCredential}>
            {errorInfo && <ErrorPanel error={errorInfo}/>}
            <ConnectExistingGitCredentialDrawerInner {...rest} onError={setError} selectedGitCredential={selectedGitCredential} setSelectedGitCredential={setSelectedGitCredential} isSaving={isSaving}/>
        </Drawer>);
};
